import { isProblemDetails } from '@hpd/contract';
import type { components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ VEN-11 at v1.988 (venue slice 4A): a venue's agreement, its supply model and margins per
// line type (FIN-17). Rates travel as basis points (1500 is 15%) so nothing is a float; the screen
// shows and takes percentages, converted exactly here.

export type AgreementRecord = components['schemas']['AgreementRecordView'];
export type Agreement = components['schemas']['AgreementView'];
export type AgreementSupplyModel = Agreement['supplyModel'];
export type Settlement = Agreement['settlement'];
export type LineType = keyof components['schemas']['AgreementMarginsView'];
export type SupplyModel = components['schemas']['QuoteView']['supplyModel'];

export const LINE_TYPE_LABELS: Record<LineType, string> = {
  hire: 'Hire',
  sale: 'Sale',
  labour: 'Labour',
  transport: 'Transport',
  cross_hire: 'Cross-hire',
  incidentals: 'Incidentals',
};
export const LINE_TYPES = Object.keys(LINE_TYPE_LABELS) as LineType[];

export const SUPPLY_MODEL_LABELS: Record<AgreementSupplyModel | 'standard', string> = {
  standard: 'Standard',
  resale: 'Resale',
  direct_bill: 'Direct bill',
  agency: 'Agency',
};

export const SETTLEMENT_LABELS: Record<Settlement, string> = {
  per_job: 'Per job',
  monthly: 'Monthly',
};

export type AllocationTerms = components['schemas']['AllocationTermsView'];

export interface AgreementInput {
  supplyModel: AgreementSupplyModel;
  marginsBp: Partial<Record<LineType, number>>;
  venueEquipmentCommissionBp: number;
  settlement: Settlement;
  setOff: boolean;
  rcti: boolean;
  /** VEN-11 at v1.992: the complimentary allocation, or none. */
  allocation: AllocationTerms | null;
}

/** "12.5" → 1250; null when it is not a percentage from 0 to 100 with at most two decimals. */
export function percentToBp(text: string): number | null {
  const t = text.trim();
  if (t === '') return 0;
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return null;
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  return bp <= 10_000 ? bp : null;
}

/** 1250 → "12.5"; 0 → "". */
export function bpToPercent(bp: number | undefined): string {
  if (!bp) return '';
  const whole = Math.floor(bp / 100);
  const rest = bp % 100;
  return rest === 0 ? String(whole) : `${whole}.${String(rest).padStart(2, '0').replace(/0$/, '')}`;
}

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const agreementQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'agreement'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/agreement', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load the agreement.');
      return data;
    },
  });

export async function saveAgreement(venueId: string, input: AgreementInput) {
  const { data, error } = await api.POST('/v1/venues/{id}/agreement', {
    params: { path: { id: venueId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not save the agreement.');
  return data;
}

export async function setQuoteSupplyModel(quoteId: string, supplyModel: SupplyModel) {
  const { data, error } = await api.PUT('/v1/quotes/{id}/supply-model', {
    params: { path: { id: quoteId } },
    body: { supplyModel },
  });
  if (error) throw problem(error, 'Could not change how the venue is billed.');
  return data;
}

// CB-REQ VEN-11 at v1.989 (venue slice 4B): the estimate of a resale venue's invoice, and a line
// for the venue's own account.

export type VenueMargin = components['schemas']['VenueMarginView'];

export const venueMarginQuery = (quoteId: string) =>
  queryOptions({
    queryKey: ['quotes', quoteId, 'venue-margin'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/quotes/{id}/venue-margin', {
        params: { path: { id: quoteId } },
      });
      if (error) throw problem(error, 'Could not estimate the venue’s margin.');
      return data.margin ?? null;
    },
  });

export async function setForVenueAccount(
  quoteId: string,
  lineId: string,
  forVenueAccount: boolean,
) {
  const { data, error } = await api.PUT('/v1/quotes/{id}/lines/{lineId}/for-venue-account', {
    params: { path: { id: quoteId, lineId } },
    body: { forVenueAccount },
  });
  if (error) throw problem(error, 'Could not change that line.');
  return data;
}

/** The same from a job, by someone who may manage jobs. */
export async function setJobLineForVenueAccount(
  jobId: string,
  quoteLineId: string,
  forVenueAccount: boolean,
) {
  const { error } = await api.PUT('/v1/jobs/{id}/lines/{quoteLineId}/for-venue-account', {
    params: { path: { id: jobId, quoteLineId } },
    body: { forVenueAccount },
  });
  if (error) throw problem(error, 'Could not change that line.');
}

// CB-REQ VEN-11 at v1.992 (venue slice 4D): a venue's complimentary allocation — this period's
// balance, and what a venue's own quote would draw.

export type AllocationBalance = components['schemas']['AllocationBalanceView'];
export type AllocationPreview = components['schemas']['AllocationPreviewView'];

export const allocationQuery = (venueId: string, on: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'allocation', on],
    queryFn: async () => {
      const { data, error, response } = await api.GET('/v1/venues/{id}/allocation', {
        params: { path: { id: venueId }, query: { on } },
      });
      // None applies on that day: not an error to show.
      if (response.status === 404) return null;
      if (error) throw problem(error, 'Could not load the allocation.');
      return data;
    },
  });

export const quoteAllocationQuery = (quoteId: string) =>
  queryOptions({
    queryKey: ['quotes', quoteId, 'allocation'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/quotes/{id}/allocation', {
        params: { path: { id: quoteId } },
      });
      if (error) throw problem(error, 'Could not work out the allocation.');
      return data.allocation ?? null;
    },
  });
